import React, { useState, useEffect, useCallback } from 'react';
import { getPortfolio, getOrders, quickTrade, getPortfolioIndicators, getPortfolioPerformance } from '../api';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine, ReferenceDot } from 'recharts';

export default function DashboardPage({ alpaca }) {
  const pv = Number(alpaca?.portfolio_value || 0);
  const bp = Number(alpaca?.buying_power || 0);
  const cash = Number(alpaca?.cash || 0);

  const [positions, setPositions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [indicators, setIndicators] = useState({});

  const [marketOpen, setMarketOpen] = useState(null);

  const refresh = () => {
    getPortfolio().then(d => {
      const pos = d.positions || [];
      setPositions(pos);
      if (pos.length) {
        getPortfolioIndicators(pos.map(p => p.symbol))
          .then(setIndicators)
          .catch(() => {});
      }
    }).catch(() => {});
    getOrders().then(d => setOrders((d.orders || []).slice(0, 10))).catch(() => {});
  };

  useEffect(() => {
    refresh();
    // Check market status
    fetch(`${process.env.REACT_APP_API_URL || ''}/api/alpaca/status`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setMarketOpen(d.market_open ?? null))
      .catch(() => {});
  }, []);

  const totalValue = positions.reduce((s, p) => s + (p.market_value || 0), 0);
  const totalPnl = positions.reduce((s, p) => s + (p.unrealized_pnl || 0), 0);
  const totalQty = positions.reduce((s, p) => s + (p.qty || 0), 0);
  const totalPnlPct = totalValue > 0 ? (totalPnl / (totalValue - totalPnl)) * 100 : 0;

  const indicatorDate = Object.values(indicators).find(i => i?.date)?.date || '';

  return (
    <div>
      {/* Header */}
      <div style={s.header}>
        <div>
          <h1 style={s.h1}>Portfolio Overview</h1>
          <p style={s.sub}>Paper Trading · Account {alpaca?.account_id || '—'}{indicatorDate && ` · ${indicatorDate}`}</p>
        </div>
        <div style={{ ...s.marketBadge, ...(marketOpen ? { borderColor: 'rgba(16,185,129,0.3)' } : {}) }}>
          <span style={{ ...s.dot, background: marketOpen ? 'var(--green)' : 'var(--t3)' }} />
          {marketOpen === null ? 'Checking...' : marketOpen ? 'Market Open' : 'Market Closed'}
        </div>
      </div>

      {/* Metric cards */}
      <div style={s.metrics}>
        <div style={{ ...s.metricCard, borderLeft: '3px solid var(--green)' }}>
          <div style={s.metricLabel}>Portfolio Value</div>
          <div style={s.metricBig}>{fmt(pv)}</div>
        </div>
        <div style={s.metricCard}>
          <div style={s.metricLabel}>Cash Available</div>
          <div style={s.metricValue}>{fmt(cash)}</div>
          <div style={s.metricSub}>{pv > 0 ? `${((cash / pv) * 100).toFixed(0)}% of portfolio` : ''}</div>
        </div>
        <div style={s.metricCard}>
          <div style={s.metricLabel}>Buying Power</div>
          <div style={s.metricValue}>{fmt(bp)}</div>
        </div>
        <div style={{ ...s.metricCard, borderLeft: `3px solid ${totalPnl >= 0 ? 'var(--green)' : 'var(--red)'}` }}>
          <div style={s.metricLabel}>Unrealized P&L</div>
          <div style={{ ...s.metricValue, color: totalPnl >= 0 ? 'var(--green)' : 'var(--red)' }}>
            {totalPnl >= 0 ? '+' : ''}{fmt(totalPnl)}
          </div>
          <div style={{ ...s.metricSub, color: totalPnl >= 0 ? 'var(--green)' : 'var(--red)', opacity: 0.85 }}>
            {totalPnlPct >= 0 ? '+' : ''}{totalPnlPct.toFixed(2)}%
          </div>
        </div>
      </div>

      {/* Performance Chart */}
      <PerformanceChart positions={positions} />

      {/* Quick Trade */}
      <QuickTrade onSuccess={refresh} />

      {/* Positions */}
      <div style={s.section}>
        <div style={s.sectionHead}>
          <h2 style={s.sectionTitle}>Open Positions</h2>
          <span style={s.count}>{positions.length} {positions.length === 1 ? 'stock' : 'stocks'}</span>
        </div>
        {positions.length ? (
          <div>
            <div style={s.posHead}>
              <span style={s.colSymbol}>Symbol</span>
              <span style={s.colQty}>Shares</span>
              <span style={s.colPrice}>Avg Cost</span>
              <span style={s.colPrice}>Price</span>
              <span style={s.colValue}>Value</span>
              <span style={s.colPnl}>P&L</span>
              <span style={s.colTrade}>Trade</span>
            </div>
            {positions.map((p, i) => (
              <PositionRow key={p.symbol} position={p} onTrade={refresh} even={i % 2 === 0} indicators={indicators[p.symbol]} />
            ))}
            {/* Total row */}
            <div style={s.totalRow}>
              <span style={s.colSymbol}>Total</span>
              <span style={s.colQty}>{totalQty}</span>
              <span style={s.colPrice}></span>
              <span style={s.colPrice}></span>
              <span style={s.colValue}>{fmt(totalValue)}</span>
              <span style={s.colPnl}>
                {totalPnl >= 0 ? '+' : ''}{fmt(totalPnl)}
                <span style={{ fontSize: 11, marginLeft: 3, opacity: 0.7 }}>({totalPnlPct >= 0 ? '+' : ''}{totalPnlPct.toFixed(1)}%)</span>
              </span>
              <span style={s.colTrade}></span>
            </div>
          </div>
        ) : (
          <div style={s.empty}>
            <div style={{ fontSize: 28, marginBottom: 10, opacity: 0.4 }}>📊</div>
            <p style={s.emptyTitle}>No Open Positions</p>
            <p style={s.emptyText}>Review signal-based order drafts on the Order Review page to get started.</p>
          </div>
        )}
      </div>

      {/* Recent Activity */}
      <div style={s.section}>
        <div style={s.sectionHead}>
          <h2 style={s.sectionTitle}>Recent Activity</h2>
          <span style={s.count}>{orders.length}</span>
        </div>
        {orders.length ? (
          <div>
            {orders.map((o, i) => (
              <div key={o.id || i} style={{ ...s.actRow, background: i % 2 === 0 ? 'transparent' : 'var(--bg-2)' }}>
                <span style={s.actDate}>{(o.submitted_at || '').slice(0, 10)}</span>
                <span style={{ ...s.actSide, color: o.side === 'BUY' ? 'var(--green)' : 'var(--red)' }}>{o.side}</span>
                <span style={s.actSymbol}>{o.symbol}</span>
                <span style={s.actQty}>{o.qty}</span>
                <span style={s.actPrice}>{o.price ? fmt(o.price) : '—'}</span>
                <span style={{ marginLeft: 'auto' }}>
                  <span style={{
                    fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 4,
                    background: o.status === 'FILLED' ? 'var(--green-bg)' : o.status === 'CANCELED' ? 'var(--bg-3)' : 'var(--amber-bg)',
                    color: o.status === 'FILLED' ? 'var(--green)' : o.status === 'CANCELED' ? 'var(--t3)' : 'var(--amber)',
                  }}>{o.status}</span>
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div style={s.empty}>
            <div style={{ fontSize: 28, marginBottom: 10, opacity: 0.4 }}>📜</div>
            <p style={s.emptyTitle}>No Recent Activity</p>
            <p style={s.emptyText}>Orders will appear here after you submit decisions from Order Review.</p>
          </div>
        )}
      </div>
    </div>
  );
}

const PERIODS = ['1W', '1M', '3M', '6M', '1Y', 'ALL'];

function PerformanceChart({ positions: livePositions }) {
  const [rawData, setRawData] = useState([]);
  const [period, setPeriod] = useState('1Y');
  const [mode, setMode] = useState('dollar'); // 'dollar' | 'percent'
  const [loading, setLoading] = useState(true);
  const [hover, setHover] = useState(null);

  const positionsReady = livePositions && livePositions.length > 0;

  useEffect(() => {
    if (!positionsReady) return;
    setLoading(true);
    getPortfolioPerformance(period)
      .then(d => setRawData((d.data || []).filter(pt => !pt.live)))
      .catch(() => setRawData([]))
      .finally(() => setLoading(false));
  }, [period, positionsReady]);

  // Compute live point from positions prop at render time (same data as P&L card)
  const data = React.useMemo(() => {
    if (!rawData.length) return [];
    const pts = rawData.map(pt => ({
      ...pt,
      pnl_dollar: pt.equity - pt.cost_basis,
      pnl_percent: pt.cost_basis > 0 ? ((pt.equity - pt.cost_basis) / pt.cost_basis) * 100 : 0,
    }));
    if (livePositions && livePositions.length > 0) {
      const today = new Date().toISOString().slice(0, 10);
      const lastDate = pts[pts.length - 1].date;
      if (today > lastDate) {
        const liveCostBasis = livePositions.reduce((s, p) => s + (p.qty * p.avg_cost), 0);
        const liveMarketValue = livePositions.reduce((s, p) => s + (p.market_value || 0), 0);
        const livePnl = livePositions.reduce((s, p) => s + (p.unrealized_pnl || 0), 0);
        const livePnlPct = liveCostBasis > 0 ? (livePnl / liveCostBasis) * 100 : 0;
        const roundedCB = Math.round(liveCostBasis * 100) / 100;
        // Update last historical point's cost basis so grey line drops before live dot
        const lastPt = pts[pts.length - 1];
        if (Math.abs(lastPt.cost_basis - roundedCB) > 1) {
          pts[pts.length - 1] = { ...lastPt, cost_basis: roundedCB };
        }
        pts.push({
          date: today,
          equity: Math.round(liveMarketValue * 100) / 100,
          cost_basis: roundedCB,
          pnl: Math.round(livePnl * 100) / 100,
          pnl_pct: Math.round(livePnlPct * 100) / 100,
          pnl_dollar: Math.round(livePnl * 100) / 100,
          pnl_percent: Math.round(livePnlPct * 100) / 100,
        });
      }
    }
    return pts;
  }, [rawData, livePositions]);

  const latest = data.length > 0 ? data[data.length - 1] : null;
  const first = data.length > 0 ? data[0] : null;
  const totalReturn = latest && first ? latest.equity - first.equity : 0;
  const totalReturnPct = first && first.equity > 0 ? (totalReturn / first.equity) * 100 : 0;
  const pnlDollar = latest ? latest.pnl_dollar : 0;
  const pnlPct = latest ? latest.pnl_percent : 0;
  const isPositive = pnlDollar >= 0;

  const hoverData = hover || latest;

  return (
    <div style={s.chartSection}>
      {/* Header row */}
      <div style={s.chartHeader}>
        <div>
          <div style={s.chartTitle}>Portfolio Performance</div>
          {hoverData && (
            <div style={s.chartBig}>
              <span style={{ fontSize: 22, fontWeight: 700, color: (hoverData.pnl_dollar) >= 0 ? 'var(--green)' : 'var(--red)' }}>
                {hoverData.pnl_dollar >= 0 ? '+' : ''}
                {mode === 'dollar'
                  ? `$${hoverData.pnl_dollar.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                  : `${hoverData.pnl_percent.toFixed(2)}%`
                }
              </span>
              <span style={s.chartDate}>{hoverData.date}</span>
            </div>
          )}
          {latest && (
            <div style={s.chartMeta}>
              <span>Market Value: <b>${latest.equity.toLocaleString(undefined, { maximumFractionDigits: 0 })}</b></span>
              <span style={{ margin: '0 8px', color: 'var(--border)' }}>|</span>
              <span>Cost Basis: <b>${latest.cost_basis.toLocaleString(undefined, { maximumFractionDigits: 0 })}</b></span>
              <span style={{ margin: '0 8px', color: 'var(--border)' }}>|</span>
              <span>P&L: <span style={{ color: pnlDollar >= 0 ? 'var(--green)' : 'var(--red)', fontWeight: 700 }}>
                {pnlDollar >= 0 ? '+' : ''}${pnlDollar.toLocaleString(undefined, { maximumFractionDigits: 0 })} ({pnlPct >= 0 ? '+' : ''}{pnlPct.toFixed(2)}%)
              </span></span>
            </div>
          )}
        </div>
        <div style={s.chartControls}>
          {/* $ / % toggle */}
          <div style={s.modeToggle}>
            <button onClick={() => setMode('dollar')} style={{ ...s.modeBtn, ...(mode === 'dollar' ? s.modeBtnActive : {}) }}>$</button>
            <button onClick={() => setMode('percent')} style={{ ...s.modeBtn, ...(mode === 'percent' ? s.modeBtnActive : {}) }}>%</button>
          </div>
          {/* Period pills */}
          <div style={s.periodBar}>
            {PERIODS.map(p => (
              <button key={p} onClick={() => setPeriod(p)}
                style={{ ...s.periodBtn, ...(period === p ? s.periodBtnActive : {}) }}>
                {p}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Chart */}
      <div style={{ height: 280, padding: '0 4px' }}>
        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <div style={{ color: 'var(--t3)', fontSize: 13 }}>Loading chart...</div>
          </div>
        ) : data.length === 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <div style={{ color: 'var(--t3)', fontSize: 13 }}>No performance data available</div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data}
              onMouseMove={(e) => { if (e?.activePayload?.[0]) setHover(e.activePayload[0].payload); }}
              onMouseLeave={() => setHover(null)}
              margin={{ top: 8, right: 12, left: 12, bottom: 0 }}>
              <defs>
                <linearGradient id="pnlGradientGreen" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="rgb(16,185,129)" stopOpacity={0.15} />
                  <stop offset="100%" stopColor="rgb(16,185,129)" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="pnlGradientRed" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="rgb(239,68,68)" stopOpacity={0.15} />
                  <stop offset="100%" stopColor="rgb(239,68,68)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-s)" vertical={false} />
              <XAxis
                dataKey="date" tick={{ fontSize: 10, fill: 'var(--t3)' }}
                tickLine={false} axisLine={{ stroke: 'var(--border-s)' }}
                tickFormatter={(d) => { const parts = d.split('-'); return `${parts[1]}/${parts[2]}`; }}
                interval={Math.max(0, Math.floor(data.length / 6))}
              />
              <YAxis
                tick={{ fontSize: 10, fill: 'var(--t3)' }}
                tickLine={false} axisLine={false}
                tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`}
                width={52}
                domain={['auto', 'auto']}
              />
              <Tooltip
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const d = payload[0].payload;
                  const pnl = d.equity - d.cost_basis;
                  const pnlP = d.cost_basis > 0 ? (pnl / d.cost_basis) * 100 : 0;
                  const pos = pnl >= 0;
                  return (
                    <div style={s.tooltip}>
                      <div style={s.tooltipDate}>{d.date}</div>
                      <div style={{ color: pos ? 'var(--green)' : 'var(--red)', fontWeight: 700, fontSize: 14 }}>
                        {pos ? '+' : ''}${pnl.toLocaleString(undefined, { maximumFractionDigits: 2 })} ({pos ? '+' : ''}{pnlP.toFixed(2)}%)
                      </div>
                      <div style={s.tooltipRow}>Market Value: ${d.equity.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
                      <div style={s.tooltipRow}>Cost Basis: ${d.cost_basis.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
                    </div>
                  );
                }}
              />
              <Area
                type="stepAfter" dataKey="cost_basis"
                stroke="rgba(150,150,160,0.6)" strokeWidth={3}
                fill="none" dot={false} activeDot={false}
                connectNulls
              />
              {/* Hidden area just for Y-axis scaling */}
              <Area type="linear" dataKey="equity" stroke="none" fill="none" dot={false} activeDot={false} />
              {/* Per-segment colored lines + dots */}
              {data.length > 1 && data.slice(1).map((d, i) => {
                const prev = data[i];
                const midEquity = (prev.equity + d.equity) / 2;
                const midCost = (prev.cost_basis + d.cost_basis) / 2;
                const color = midEquity >= midCost ? 'rgb(16,185,129)' : 'rgb(239,68,68)';
                return (
                  <ReferenceLine
                    key={`seg-${i}`}
                    segment={[{ x: prev.date, y: prev.equity }, { x: d.date, y: d.equity }]}
                    stroke={color} strokeWidth={2}
                  />
                );
              })}
              {data.map((d, i) => {
                const color = d.equity >= d.cost_basis ? 'rgb(16,185,129)' : 'rgb(239,68,68)';
                return (
                  <ReferenceDot
                    key={`dot-${i}`}
                    x={d.date} y={d.equity}
                    r={3} fill={color} stroke="var(--bg-1)" strokeWidth={2}
                  />
                );
              })}
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

function QuickTrade({ onSuccess }) {
  const [symbol, setSymbol] = useState('');
  const [qty, setQty] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (side) => {
    if (!symbol.trim() || qty < 1) return;
    setSubmitting(true);
    setResult(null);
    try {
      const res = await quickTrade(symbol.trim().toUpperCase(), side, qty);
      setResult({ ok: true, msg: `${side.toUpperCase()} ${qty} ${res.symbol} — order ${res.order_id.slice(0, 8)}…` });
      setSymbol('');
      setQty(1);
      setTimeout(() => onSuccess?.(), 1500);
    } catch (err) {
      setResult({ ok: false, msg: err.message || 'Order failed' });
    }
    setSubmitting(false);
  };

  return (
    <div style={s.section}>
      <div style={s.sectionHead}>
        <h2 style={s.sectionTitle}>Quick Trade</h2>
        <span style={{ fontSize: 11, color: 'var(--t3)' }}>Market order · Paper trading</span>
      </div>
      <div style={s.tradeForm}>
        <div style={s.tradeField}>
          <label style={s.tradeLabel}>Symbol</label>
          <input value={symbol} onChange={e => setSymbol(e.target.value.toUpperCase())}
            placeholder="AAPL" style={s.tradeInput} disabled={submitting} />
        </div>
        <div style={s.tradeField}>
          <label style={s.tradeLabel}>Quantity</label>
          <input type="number" min="1" value={qty}
            onChange={e => setQty(parseInt(e.target.value) || 1)}
            style={{ ...s.tradeInput, width: 80, textAlign: 'center' }} disabled={submitting} />
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <button onClick={() => submit('buy')} disabled={submitting || !symbol.trim()}
            style={{ ...s.tradeBtn, background: 'var(--green)', color: '#000' }}>
            {submitting ? '…' : 'Buy'}
          </button>
          <button onClick={() => submit('sell')} disabled={submitting || !symbol.trim()}
            style={{ ...s.tradeBtn, background: 'var(--red)', color: '#fff' }}>
            {submitting ? '…' : 'Sell'}
          </button>
        </div>
      </div>
      {result && (
        <div style={{
          padding: '10px 24px 14px', fontSize: 13,
          color: result.ok ? 'var(--green)' : 'var(--red)',
        }}>
          {result.ok ? '✓' : '✗'} {result.msg}
        </div>
      )}
    </div>
  );
}

function PositionRow({ position: p, onTrade, even, indicators }) {
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const pnlPct = p.unrealized_pct || 0;
  const ind = indicators || {};

  const trade = async (side) => {
    if (qty < 1) return;
    setBusy(true);
    setMsg(null);
    try {
      await quickTrade(p.symbol, side, qty);
      setMsg({ ok: true, text: `${side.toUpperCase()} ${qty}` });
      setTimeout(() => { setMsg(null); onTrade?.(); }, 1500);
    } catch (err) {
      setMsg({ ok: false, text: err.message || 'Failed' });
    }
    setBusy(false);
  };

  const KEY_INDICATORS = [
    ['rsi', 'RSI(14)'], ['macd', 'MACD'], ['macd_signal', 'Signal'], ['macd_hist', 'Histogram'],
    ['sma_50', 'SMA 50'], ['sma_200', 'SMA 200'], ['ema_20', 'EMA 20'],
    ['bb_upper', 'BB Upper'], ['bb_lower', 'BB Lower'],
    ['stoch_k', 'Stoch K'], ['stoch_d', 'Stoch D'], ['willr', 'Williams %R'],
    ['cci', 'CCI'], ['mfi', 'MFI'], ['atr', 'ATR'], ['obv', 'OBV'],
    ['vwap', 'VWAP'], ['zscore', 'Z-Score'], ['roc', 'ROC'], ['trix', 'TRIX'],
    ['volume', 'Volume'], ['vol_sma_20', 'Vol SMA 20'],
  ];

  const fmtInd = (key, val) => {
    if (val == null) return '—';
    if (key === 'obv' || key === 'volume' || key === 'vol_sma_20') return `${(val / 1e6).toFixed(1)}M`;
    return typeof val === 'number' ? val.toFixed(2) : String(val);
  };

  const indColor = (key, val) => {
    if (val == null) return 'var(--t1)';
    if (key === 'rsi') return val > 70 ? 'var(--red)' : val < 30 ? 'var(--green)' : 'var(--t1)';
    if (key === 'mfi') return val > 80 ? 'var(--red)' : val < 20 ? 'var(--green)' : 'var(--t1)';
    if (key === 'stoch_k' || key === 'stoch_d') return val > 80 ? 'var(--red)' : val < 20 ? 'var(--green)' : 'var(--t1)';
    if (key === 'willr') return val > -20 ? 'var(--red)' : val < -80 ? 'var(--green)' : 'var(--t1)';
    if (key === 'cci') return val > 100 ? 'var(--red)' : val < -100 ? 'var(--green)' : 'var(--t1)';
    if (key === 'zscore') return val > 1.5 ? 'var(--red)' : val < -1.5 ? 'var(--green)' : 'var(--t1)';
    if (key === 'macd_hist') return val > 0 ? 'var(--green)' : val < 0 ? 'var(--red)' : 'var(--t1)';
    if (key === 'roc') return val > 5 ? 'var(--green)' : val < -5 ? 'var(--red)' : 'var(--t1)';
    if (key === 'trix') return val > 0 ? 'var(--green)' : val < 0 ? 'var(--red)' : 'var(--t1)';
    return 'var(--t1)';
  };

  return (
    <>
      <div style={{ ...s.posRow, background: even ? 'transparent' : 'var(--bg-2)', cursor: 'pointer' }} onClick={() => setExpanded(!expanded)}>
        <span style={s.colSymbol}>
          {p.symbol}
          <span style={{ fontSize: 9, color: 'var(--t3)', marginLeft: 4 }}>{expanded ? '▾' : '▸'}</span>
        </span>
        <span style={s.colQty}>{p.qty}</span>
        <span style={s.colPrice}>{fmt(p.avg_cost || 0)}</span>
        <span style={s.colPrice}>{fmt(p.current_price || 0)}</span>
        <span style={s.colValue}>{fmt(p.market_value || 0)}</span>
        <span style={{ ...s.colPnl, color: p.unrealized_pnl >= 0 ? 'var(--green)' : 'var(--red)' }}>
          {p.unrealized_pnl >= 0 ? '+' : ''}{fmt(p.unrealized_pnl || 0)}
          <span style={{ fontSize: 10, marginLeft: 3, opacity: 0.7 }}>({pnlPct >= 0 ? '+' : ''}{pnlPct.toFixed(1)}%)</span>
        </span>
        <span style={s.colTrade} onClick={e => e.stopPropagation()}>
          {msg ? (
            <span style={{ fontSize: 12, color: msg.ok ? 'var(--green)' : 'var(--red)' }}>
              {msg.ok ? '✓' : '✗'} {msg.text}
            </span>
          ) : (
            <span style={{ display: 'flex', gap: 4, alignItems: 'center', justifyContent: 'flex-end' }}>
              <input type="number" min="1" value={qty}
                onChange={e => setQty(parseInt(e.target.value) || 1)}
                style={s.posQtyInput} disabled={busy} />
              <button onClick={() => trade('buy')} disabled={busy} style={s.posTradeBtnBuy}>Buy</button>
              <button onClick={() => trade('sell')} disabled={busy} style={s.posTradeBtnSell}>Sell</button>
            </span>
          )}
        </span>
      </div>
      {expanded && (
        <div style={s.indPanel}>
          {ind.error ? (
            <span style={{ color: 'var(--t3)', fontSize: 11 }}>Failed to load indicators</span>
          ) : !ind.date ? (
            <span style={{ color: 'var(--t3)', fontSize: 11 }}>Loading...</span>
          ) : (
            <>
              <div style={s.indDate}>As of {ind.date} · {ind.close != null ? `Close: $${Number(ind.close).toFixed(2)}` : ''}</div>
              <div style={s.indGrid}>
                {KEY_INDICATORS.map(([key, label]) => (
                  <div key={key} style={s.indItem}>
                    <span style={s.indLabel}>{label}</span>
                    <span style={{ ...s.indValue, color: indColor(key, ind[key]) }}>{fmtInd(key, ind[key])}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}

function fmt(v) {
  return `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const s = {
  // Chart
  chartSection: {
    background: 'var(--bg-1)', borderRadius: 'var(--rl)', border: '1px solid var(--border)',
    marginBottom: 16, padding: '20px 20px 16px', overflow: 'hidden',
  },
  chartHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    marginBottom: 16,
  },
  chartTitle: { fontSize: 10, fontWeight: 600, color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 },
  chartBig: { display: 'flex', alignItems: 'baseline', gap: 10 },
  chartDate: { fontSize: 11, color: 'var(--t3)', fontWeight: 400 },
  chartMeta: { fontSize: 11, color: 'var(--t3)', marginTop: 6 },
  chartControls: { display: 'flex', alignItems: 'center', gap: 10 },
  modeToggle: {
    display: 'flex', background: 'var(--bg-2)', borderRadius: 6,
    border: '1px solid var(--border)', overflow: 'hidden',
  },
  modeBtn: {
    padding: '5px 12px', fontSize: 12, fontWeight: 700, border: 'none',
    background: 'transparent', color: 'var(--t3)', cursor: 'pointer',
  },
  modeBtnActive: {
    background: 'var(--green)', color: '#000',
  },
  periodBar: {
    display: 'flex', background: 'var(--bg-2)', borderRadius: 6,
    border: '1px solid var(--border)', overflow: 'hidden',
  },
  periodBtn: {
    padding: '5px 10px', fontSize: 10, fontWeight: 600, border: 'none',
    background: 'transparent', color: 'var(--t3)', cursor: 'pointer',
    letterSpacing: 0.3,
  },
  periodBtnActive: {
    background: 'var(--bg-3)', color: 'var(--t1)',
  },
  tooltip: {
    background: 'var(--bg-1)', border: '1px solid var(--border)',
    borderRadius: 8, padding: '10px 14px', boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
    fontSize: 12,
  },
  tooltipDate: { fontSize: 10, color: 'var(--t3)', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 },
  tooltipRow: { fontSize: 11, color: 'var(--t2)', marginTop: 3 },

  // Dashboard
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 },
  h1: { fontSize: 22, fontWeight: 700, marginBottom: 4 },
  sub: { fontSize: 13, color: 'var(--t3)' },
  marketBadge: {
    display: 'flex', alignItems: 'center', gap: 8, padding: '6px 14px',
    background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: 20,
    fontSize: 12, color: 'var(--t3)', fontWeight: 500,
  },
  dot: { width: 6, height: 6, borderRadius: '50%', background: 'var(--t3)' },

  metrics: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 },
  metricCard: {
    background: 'var(--bg-1)', borderRadius: 'var(--r)', border: '1px solid var(--border)',
    padding: '16px 20px',
  },
  metricLabel: {
    fontSize: 10, fontWeight: 600, color: 'var(--t3)', textTransform: 'uppercase',
    letterSpacing: 0.5, marginBottom: 6,
  },
  metricBig: { fontSize: 20, fontWeight: 700, color: 'var(--t1)' },
  metricValue: { fontSize: 18, fontWeight: 700, color: 'var(--t1)' },
  metricSub: { fontSize: 11, color: 'var(--t3)', marginTop: 3 },

  section: {
    background: 'var(--bg-1)', borderRadius: 'var(--rl)', border: '1px solid var(--border)',
    marginBottom: 16, overflow: 'hidden',
  },
  sectionHead: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '14px 20px', borderBottom: '1px solid var(--border-s)',
  },
  sectionTitle: { fontSize: 14, fontWeight: 600 },
  count: {
    fontSize: 11, fontWeight: 600, color: 'var(--t3)', background: 'var(--bg-3)',
    padding: '2px 10px', borderRadius: 10,
  },

  colSymbol: { width: 65, fontWeight: 600 },
  colQty: { width: 45, textAlign: 'right' },
  colPrice: { width: 80, textAlign: 'right', color: 'var(--t2)' },
  colValue: { width: 90, textAlign: 'right', fontWeight: 600 },
  colPnl: { width: 130, textAlign: 'right', fontWeight: 600 },
  colTrade: { flex: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' },

  posHead: {
    display: 'flex', gap: 12, padding: '10px 20px',
    fontSize: 10, fontWeight: 600, color: 'var(--t3)', textTransform: 'uppercase',
    letterSpacing: 0.5, borderBottom: '1px solid var(--border-s)', background: 'var(--bg-2)',
  },
  posRow: {
    display: 'flex', gap: 12, padding: '10px 20px', fontSize: 13,
    borderBottom: '1px solid var(--border-s)', alignItems: 'center',
  },
  totalRow: {
    display: 'flex', gap: 12, padding: '12px 20px', fontSize: 13, fontWeight: 700,
    borderTop: '2px solid var(--border)', alignItems: 'center',
    background: 'var(--bg-2)',
  },
  posQtyInput: {
    width: 42, padding: '3px 4px', background: 'var(--bg-2)', border: '1px solid var(--border)',
    borderRadius: 4, color: 'var(--t1)', fontSize: 12, textAlign: 'center',
  },
  posTradeBtnBuy: {
    padding: '3px 10px', border: 'none', borderRadius: 4,
    fontSize: 11, fontWeight: 700, cursor: 'pointer',
    background: 'var(--green-bg)', color: 'var(--green)',
  },
  posTradeBtnSell: {
    padding: '3px 10px', border: 'none', borderRadius: 4,
    fontSize: 11, fontWeight: 700, cursor: 'pointer',
    background: 'var(--red-bg)', color: 'var(--red)',
  },

  actRow: {
    display: 'flex', gap: 12, padding: '10px 20px', fontSize: 13,
    borderBottom: '1px solid var(--border-s)', alignItems: 'center',
  },
  actDate: { width: 90, color: 'var(--t3)', fontSize: 12 },
  actSide: { width: 40, fontSize: 11, fontWeight: 700 },
  actSymbol: { width: 60, fontWeight: 600 },
  actQty: { width: 35, textAlign: 'right' },
  actPrice: { width: 80, textAlign: 'right', color: 'var(--t2)', fontSize: 12 },

  empty: { padding: '40px 20px', textAlign: 'center' },
  emptyTitle: { fontSize: 14, fontWeight: 600, marginBottom: 4, color: 'var(--t2)' },
  emptyText: { fontSize: 12, color: 'var(--t3)', lineHeight: 1.5 },

  tradeForm: { display: 'flex', gap: 14, padding: '14px 20px', alignItems: 'flex-end' },
  tradeField: { display: 'flex', flexDirection: 'column', gap: 4 },
  tradeLabel: { fontSize: 10, fontWeight: 600, color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: 0.5 },
  tradeInput: {
    padding: '8px 12px', background: 'var(--bg-2)', border: '1px solid var(--border)',
    borderRadius: 6, color: 'var(--t1)', fontSize: 13, width: 120,
  },
  tradeBtn: {
    padding: '8px 20px', border: 'none', borderRadius: 6,
    fontSize: 13, fontWeight: 700, cursor: 'pointer', height: 37,
  },

  indPanel: {
    padding: '12px 20px 14px', background: 'var(--bg-2)', borderBottom: '1px solid var(--border-s)',
  },
  indDate: { fontSize: 10, color: 'var(--t3)', marginBottom: 8, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 },
  indGrid: { display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '6px 12px' },
  indItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '3px 0' },
  indLabel: { fontSize: 10, color: 'var(--t3)', fontWeight: 500 },
  indValue: { fontSize: 11, fontWeight: 600, color: 'var(--t1)' },
};
